// Import dependencies and li
import React, { useEffect, useState } from "react";
import {
  Table,
  OverlayTrigger,
  Tooltip,
  Button,
  FormControl,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";

// Import Components
import StudentsAttendanceLazyLoadingTable from "./StudentsAttendanceLazyLoadingTable";
import ModalConfirmAttendance from "../../class/modals/ModalConfirmAttendance";

// Import custom tools
import { getFullName, validateUserRoleIds, getAppUrl } from "../../global/GlobalTools";

// Import API services
import { GetClassAttendance, GetNonWorkingDays } from "../../../api/class";

const todayFormat = moment().format("ddd-DD");

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
/**
 * Description: Component to show students attendance in a table
 * @param {string} classId Class ID of the attendance
 * @param {date} date Date of the attendance
 * @returns {JSX.Element} StudentsAttendanceTable component
 */
const StudentsAttendanceTable = ({ classId, date }) => {
  // Determine if the date is today
  const isDateToday = moment(date).isSame(moment(), "day");
  // Determine if the date is before today
  const isBeforeToday = moment(
    date,
    "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
  ).isBefore(moment().startOf("day"));
  // Determine if the date is after today
  const isAfterToday = moment(date).isAfter(moment(), "day");
  // Load required variables
  const { t } = useTranslation("dashboard");
  const customStyles = {
    checkBox: {
      width: "16px",
      height: "16px",
    },
    overflowScrollY: {
      overflowY: "scroll",
      overflowX: "hidden",
    },
  };

  const [isLoadingHolidays, setIsLoadingHolidays] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);
  const [isDayActive, setIsDayActive] = useState(false);
  const [loadingStudentsAttendance, setLoadingStudentsAttendance] =
    useState(true);
  const [studentsAttendance, setStudentsAttendance] = useState([]);
  const [allStudentsSelected, setAllStudentsSelected] = useState(false);
  const [allStudentsConfirmed, setAllStudentsConfirmed] = useState(false);
  const [isSomeAttendanceConfirmed, setSomeAttendanceConfirmed] =
    useState(false);
  const [hasCheckboxCategory, setHasCheckboxCategory] = useState(false);
  const [attendanceToConfirmed, setAttendanceToConfirmed] = useState([]);
  const [showModalConfirmAttendance, setShowModalConfirmAttendance] =
    useState(false);

  /**
   * Description: Function to determine if the day is active for any category of any student
   * @param {Array} studentsAttendance
   * @returns {boolean}
   */
  const getIsDayActiveGlobal = (studentsAttendance = []) => {
    if (!studentsAttendance.length) return false;
    const dayName = date
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    // Check if any category of any student is active on the day
    return studentsAttendance.some((student) =>
      student.category_attendance?.some(
        (category) => category.days_of_the_week?.[dayName] === true,
      ),
    );
  };

  /**Initial Loading */
  useEffect(() => {
    getClassHolidays();
  }, []);

  /**
   * Description: Function to get holidays from API
   */
  const getClassHolidays = () => {
    const payload = {
      class_id: classId,
    };
    setIsLoadingHolidays(true);
    GetNonWorkingDays(payload)
      .then((response) => {
        if (response?.data) {
          const holidays = formatHolidays(response.data);
          setHolidays(holidays);
        }
      })
      .finally(() => {
        setIsLoadingHolidays(false);
      });
  };

  useEffect(() => {
    if (isLoadingHolidays) return;
    const isHoliday = getIsHoliday();
    setIsHoliday(isHoliday);
  }, [holidays]);

  useEffect(() => {
    getStudentsAttendance();
  }, [isHoliday]);

  /**
   * Description: Function to format holidays data
   * @param {array} [holidays=[]]
   * @return {array}
   */
  const formatHolidays = (holidays = []) => {
    return holidays.map((holiday) => {
      const startDateFormatted = formatDate(holiday.start_time);
      const endDateFormatted = formatDate(holiday.end_time);
      return {
        id: holiday.id,
        startDate: startDateFormatted,
        startTime: holiday.start_time,
        endDate: endDateFormatted,
        endTime: holiday.end_time,
        title: holiday.title,
        levelNames: holiday.school_levels_name,
        label: `${startDateFormatted} - ${endDateFormatted} · ${holiday.title}`,
      };
    });
  };

  /**
   * Description: Function to format timestamp to DD/MM/YYYY
   * @param {number} timestamp
   * @return {string}
   */
  const formatDate = (timestamp) => {
    const dateLocal = moment.unix(timestamp).utc();
    return dateLocal.format("DD/MM/YYYY");
  };

  /**
   * Description: Check if the current date is a holiday
   * @returns {boolean}
   */
  const getIsHoliday = () => {
    const currentDate = moment(date, "DD/MM/YYYY");

    if (!holidays.length) return false;

    return holidays.some((holiday) => {
      const startDate = moment(holiday.startDate, "DD/MM/YYYY");
      const endDate = moment(holiday.endDate, "DD/MM/YYYY");

      return (
        startDate.isSameOrBefore(currentDate, "day") &&
        endDate.isSameOrAfter(currentDate, "day")
      );
    });
  };

  // When the classId or date changes, update the filterClassAttendance state
  // useEffect(() => {
  //   // If the classId or date changes, update the filterClassAttendance state
  //   if (classId && date) {
  //     // Get students attendance
  //     getStudentsAttendance();
  //   }
  // }, [classId, date]);

  /**
   * Description: Function to get students attendance
   */
  const getStudentsAttendance = () => {
    // Set the loading state
    setLoadingStudentsAttendance(true);
    // Get students attendance
    const filters = {
      class_id: classId,
      date: getDateInUnix(date),
      only_date: 1,
    };
    GetClassAttendance(filters)
      .then((response) => {
        // Get students attendance
        const students = response?.data?.students || [];
        // Initialize students attendance
        let studentsAttendance = formatStudentsAttendance(students);
        // Know if all students have attendance on the selected date
        const allHasAttendance =
          studentsAttendance.length > 0 &&
          studentsAttendance.every((student) =>
            student.categories.every(
              (category) => category.attendance?.hasAttendance,
            ),
          );
        // Set the all students selected state
        setAllStudentsSelected(allHasAttendance);
        // Set the students attendance
        setStudentsAttendance(studentsAttendance);
        // Know if all students have attendance on the selected date
        const allConfirmedAttendance =
          studentsAttendance.length > 0 &&
          studentsAttendance.every((student) =>
            student.categories.every(
              (category) =>
                category.attendance?.confirmedAttendance &&
                category.attendance?.hasAttendance,
            ),
          );
        // Set some attendance confirmed state
        const isSomeAttendanceConfirmed = studentsAttendance.some((student) =>
          student.categories.some(
            (category) =>
              category.attendance?.confirmedAttendance ||
              category.attendance?.id,
          ),
        );
        setSomeAttendanceConfirmed(isSomeAttendanceConfirmed);
        // Set the all students confirmed state
        setAllStudentsConfirmed(allConfirmedAttendance);
        // Verify if there is at least one category of type checkbox
        const hasCheckboxCategory = studentsAttendance.some((student) =>
          student.categories.some(
            (category) => category.input_type === "checkbox",
          ),
        );
        // Set the has checkbox category state
        setHasCheckboxCategory(hasCheckboxCategory);
      })
      .catch((error) => {
        // Log the error
        console.log(error);
      })
      .finally(() => {
        // Set the loading state
        setLoadingStudentsAttendance(false);
      });
  };

  /**
   * Description: Function to format students attendance
   * @param {Array} [students=[]]
   * @return {Array} Formatted students attendance
   */
  const formatStudentsAttendance = (students = []) => {
    const isDayActive = getIsDayActiveGlobal(students);
    setIsDayActive(isDayActive);
    return students.map((student) => {
      const categoryAttendance = student?.category_attendance || [];
      const firstName = student?.first_name || "";
      const lastName = student?.last_name || "";
      return {
        student_id: student.id,
        first_name: firstName,
        last_name: lastName,
        student_name: getFullName(student, true),
        program_name: student?.school_level_name || "",
        program_level: student?.program_level || "",
        group: student?.group || "",
        grade: student?.grade || "",
        categories: formatCategories(categoryAttendance, student, isDayActive),
      };
    });
  };

  /**
   * Description: Function to format categories
   * @param {Array} [categoryAttendance=[]]
   * @param {Object} student
   * @param {boolean} isDayActive
   * @return {Array} Formatted categories
   */
  const formatCategories = (categoryAttendance = [], student, isDayActive) => {
    return categoryAttendance.map((category) => {
      const isDefaultCategory = category.id == 1 || category.flag == 1;
      const defaultCategoryName = t(
        "textStudents.class_attendance_categoryText",
      );
      const attendance = category?.attendance || [];

      return {
        id: category.id,
        name: isDefaultCategory ? defaultCategoryName : category.name,
        color: category.color,
        input_type: category.input_type,
        flag: category.flag,
        days_of_the_week: category.days_of_the_week,
        attendance: formatAttendance(
          attendance,
          student,
          category,
          isDayActive,
        ),
      };
    });
  };

  /**
   * Description: Function to format attendance
   * @param {Array} attendance
   * @param {Object} student
   * @param {Object} category
   * @param {boolean} isDayActive
   * @return {Object} Formatted attendance
   */
  const formatAttendance = (attendance, student, category, isDayActive) => {
    const dateToFind = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    // Find attendance by date
    const att = attendance.find((att) => att.date === dateToFind);
    // Determine if the checkbox should be checked
    const isValidDay = !isHoliday && isDayActive;
    // Return attendance
    return att
      ? {
          ...att,
          hasAttendance: isValidDay && (att.value === "1" || !att.value),
          confirmedAttendance: isValidDay && att.confirmed_attendances === "1",
        }
      : {
          hasAttendance: isValidDay,
          confirmedAttendance: false,
          value: category.input_type == "checkbox" ? "1" : "",
          date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
          student_id: student.id,
          category_attendance_id: category.id,
        };
  };

  /**
   * Description: Function to get a date in UNIX format
   * @param {string} dateParam
   * @return {unix} Timestamp
   */
  const getDateInUnix = (dateParam) => {
    const newDate = dateParam || date;
    const dateString = newDate.toDateString();
    const dateUnixMiliseconds = new Date(dateString).getTime();
    const dateUnixSeconds = dateUnixMiliseconds / 1000;
    return dateUnixSeconds;
  };

  /**
   * Description: Function to update the attendance of a student in the class.
   * @param {int} studentId - Student ID
   * @param {boolean} checked - Checked state
   * @param {int} categoryId - Category ID
   * @param {string} inputType - Input type
   */
  const selectStudent = (studentId, checked, categoryId, inputType) => {
    // Create a copy of the students attendance
    const students = [...studentsAttendance];
    // Find the student index
    const studentIndex = students.findIndex(
      (student) => student.student_id == studentId,
    );
    // Update the student attendance
    if (studentIndex !== -1) {
      // Find the category index
      const categoryIndex = students[studentIndex].categories?.findIndex(
        (category) =>
          category.id == categoryId && category.input_type == inputType,
      );
      // Update the category attendance
      if (categoryIndex !== -1) {
        const category = students[studentIndex].categories[categoryIndex];
        category.attendance = {
          ...category.attendance,
          hasAttendance: checked,
          confirmedAttendance: false,
          value: inputType == "checkbox" ? (checked ? "1" : "0") : checked,
        };
      }
    }
    // Set the students attendance
    setStudentsAttendance([...students]);
    // Know if all students are selected
    const allStudentsSelected =
      students.length &&
      students.every((student) =>
        student.categories.every(
          (category) => category.attendance?.hasAttendance,
        ),
      );
    setAllStudentsSelected(allStudentsSelected);
    setAllStudentsConfirmed(false);
  };

  /**
   * Description: Function to update the attendance of all students in the class.
   */
  const selectAllStudents = () => {
    const students = [...studentsAttendance];
    // update the value of attendance for all students locally
    students.forEach((student) => {
      student.categories.forEach((category) => {
        category.attendance = {
          ...category.attendance,
          hasAttendance: !allStudentsSelected,
          confirmedAttendance: false,
          value:
            category.input_type == "checkbox"
              ? !allStudentsSelected
                ? "1"
                : "0"
              : category.attendance?.value,
        };
      });
    });
    setStudentsAttendance(students);
    setAllStudentsSelected(!allStudentsSelected);
    setAllStudentsConfirmed(false);
  };

  /**
   * Description: Function to toggle the confirm save attendance button
   */
  const toggleConfirmSaveAttendanceButton = () => {
    // Create a copy of the students attendance
    const students = [...studentsAttendance];
    const categories = students[0]?.categories || [];
    // Create categories formated to confirm attendance
    const categoriesFormated = categories.map((category) => ({
      id: category.id,
      name: category.name,
      color: category.color,
      type: category.input_type,
      attendancesDays: [
        {
          dateUnix: moment(date, "ddd-DD").startOf("day").unix(),
          date: todayFormat,
          dateDay: capitalizeFirstLetter(
            moment(date, "ddd-DD").format("dddd DD-MM-YYYY"),
          ),
          students: students.flatMap((student) => {
            // Find the category of the student
            const categoryStudent = student.categories.find(
              (item) => item.id == category.id,
            );
            // Get the value of the category
            const value = categoryStudent?.attendance?.value;
            // Know if the category is a checkbox
            const isCheckbox = category.input_type === "checkbox";
            // If the category is a checkbox and the value is not true, return an empty array
            // If the category is not a checkbox and the value is empty, return an empty array
            if (
              (isCheckbox && value === "1") ||
              (!isCheckbox && value !== "")
            ) {
              return [];
            }
            // Return the student attendance
            return [
              {
                id: student.student_id,
                student_id: student.student_id,
                first_name: student.first_name,
                last_name: student.last_name,
                value: isCheckbox ? "1" : value,
              },
            ];
          }),
        },
      ],
    }));
    setAttendanceToConfirmed(categoriesFormated);
    setShowModalConfirmAttendance(true);
  };

  /**
   * Description: Function to render the students attendance table
   * @returns {JSX.Element} StudentsAttendanceTable component
   */
  return loadingStudentsAttendance ? (
    <StudentsAttendanceLazyLoadingTable />
  ) : (
    <div>
      <Table
        bordered
        responsive
        style={{
          borderTop: "white",
          borderBottom: "solid 1px #dee2e6",
        }}
      >
        <thead
          className="mt-2 mb-2"
          style={{ borderBottom: "solid 1px #dee2e6" }}
        >
          <tr>
            <th width="70%" style={{ borderRight: "none" }}></th>
            {/*Button to confirm attendance */}
            <th width="30%" style={{ borderLeft: "none", textAlign: "right" }}>
              <Button
                variant="primary"
                onClick={() => toggleConfirmSaveAttendanceButton()}
                disabled={
                  isHoliday ||
                  !isDayActive ||
                  (isSomeAttendanceConfirmed && !isDateToday) ||
                  (isBeforeToday && isSomeAttendanceConfirmed) ||
                  isAfterToday
                }
              >
                {t("textStudents.confirmAttendance")}
              </Button>
            </th>
          </tr>
          <tr style={{ borderTop: "1px" }}>
            <th width="70%">{t("textStudents.studentName")}</th>
            <th width="30%" style={{ padding: 0 }} colSpan="2">
              <Table bordered responsive style={{ marginBottom: 0 }}>
                <thead>
                  <tr className="mb-2" style={{ borderWidth: "0px 0" }}>
                    <th
                      width="20%"
                      height="35px"
                      style={{ borderLeft: "none" }}
                    ></th>
                    <th width="80%" style={{ borderRight: "none" }}>
                      <div className="d-flex justify-content-center">
                        {hasCheckboxCategory && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip
                                className="color-white"
                                id="selectAllClasses"
                              >
                                {t("textStudents.selectAll")}
                              </Tooltip>
                            }
                          >
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label="selectAllStudents"
                              checked={allStudentsSelected}
                              onChange={selectAllStudents}
                              style={{
                                ...customStyles.checkBox,
                                cursor: "pointer",
                                ...(allStudentsConfirmed && {
                                  backgroundColor: "#28a745",
                                  borderColor: "#28a745",
                                }),
                              }}
                              disabled={
                                isHoliday ||
                                !isDayActive ||
                                (isSomeAttendanceConfirmed && !isDateToday) ||
                                (isBeforeToday && isSomeAttendanceConfirmed) ||
                                isAfterToday
                              }
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
              </Table>
            </th>
          </tr>
        </thead>
        <tbody style={{ borderTop: "1px" }}>
          <tr>
            <td colSpan="2" style={{ padding: 0 }}>
              <div
                style={{
                  ...customStyles.overflowScrollY,
                  maxHeight: "171px",
                }}
                className="scrollbar-hidden"
              >
                {/**Table body - Render the students attendance table */}
                <Table bordered responsive style={{ marginBottom: 0 }}>
                  <tbody>
                    {studentsAttendance.map((student) => {
                      return (
                        <tr key={student.student_id} className="mb-2">
                          {/**Student name - Render the student name */}
                          <td width="70%" className=" align-content-center">
                            {/* If the user is a teacher, they can access the student profile from the dashboard */}
                            {validateUserRoleIds(7) ? (
                              <a
                                href={getAppUrl(`/estudiante/${student.student_id}`, true)}
                              >
                                {`${student.student_name} - `}
                                {student.grade
                                  ? `(${student.grade}${student.group})`
                                  : `(${student.program_name} ${student.program_level})`}
                              </a>
                            ) : (
                              `${student.student_name} - ` +
                              (student.grade
                                ? `(${student.grade}${student.group})`
                                : `(${student.program_name} ${student.program_level})`)
                            )}
                          </td>
                          {/**Categories attendance - Render the categories attendance table */}
                          <td width="30%" colSpan="2" style={{ padding: 0 }}>
                            {student.categories &&
                              student.categories.length > 0 &&
                              student.categories.map((category) => {
                                // Determine if the attendance is confirmed
                                const isConfirmedAttendance =
                                  category?.attendance?.confirmedAttendance;
                                const hasAttendance =
                                  category?.attendance?.hasAttendance;

                                return (
                                  <Table
                                    key={category.id}
                                    bordered
                                    responsive
                                    style={{ marginBottom: 0 }}
                                  >
                                    <tbody>
                                      {/**Categories attendance - Render the categories attendance table */}
                                      <tr className="mb-2">
                                        <td
                                          style={{
                                            height: "35px",
                                            borderLeft: "none",
                                            background: `#${category.color}`,
                                            width: "20%",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          {/**Category name - Render the category name and the tooltip */}
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={
                                              <Tooltip
                                                id={`tooltip-${category.id}`}
                                              >
                                                {category.name}
                                              </Tooltip>
                                            }
                                          >
                                            <div className="text-center">
                                              {category.name
                                                .charAt(0)
                                                .toUpperCase()}
                                            </div>
                                          </OverlayTrigger>
                                        </td>
                                        {/**Checkbox - Render the checkbox */}
                                        <td
                                          style={{
                                            borderRight: "none",
                                            width: "80%",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          <div className="d-flex justify-content-center">
                                            {category?.input_type ===
                                            "checkbox" ? (
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                aria-label="selectStudent"
                                                checked={hasAttendance}
                                                onChange={() =>
                                                  selectStudent(
                                                    student.student_id,
                                                    !hasAttendance,
                                                    category.id,
                                                    category?.input_type,
                                                  )
                                                }
                                                style={{
                                                  ...customStyles.checkBox,
                                                  cursor: "pointer",
                                                  ...(isConfirmedAttendance &&
                                                    hasAttendance && {
                                                      backgroundColor:
                                                        "#28a745",
                                                      borderColor: "#28a745",
                                                    }),
                                                }}
                                                disabled={
                                                  isHoliday ||
                                                  !isDayActive ||
                                                  (isSomeAttendanceConfirmed &&
                                                    !isDateToday) ||
                                                  (isBeforeToday &&
                                                    isConfirmedAttendance) ||
                                                  isAfterToday
                                                } // If the date is not today and all students are confirmed, or the class is not on the selected day, the checkbox is disabled or the date is in the future
                                              />
                                            ) : (
                                              <FormControl
                                                type="text"
                                                size="sm"
                                                value={
                                                  category?.attendance?.value ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  selectStudent(
                                                    student.student_id,
                                                    e.target.value,
                                                    category.id,
                                                    category?.input_type,
                                                  )
                                                }
                                                disabled={
                                                  isHoliday ||
                                                  !isDayActive ||
                                                  (isSomeAttendanceConfirmed &&
                                                    !isDateToday) ||
                                                  (isBeforeToday &&
                                                    isConfirmedAttendance) ||
                                                  isAfterToday
                                                } // If the date is not today and all students are confirmed, or the class is not on the selected day, the checkbox is disabled or the date is in the future
                                              />
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                );
                              })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </td>
          </tr>
        </tbody>
      </Table>
      {/* ConfirmAttendance */}
      {showModalConfirmAttendance && (
        <ModalConfirmAttendance
          classId={classId}
          showModalConfirmAttendance={showModalConfirmAttendance}
          onClose={() => setShowModalConfirmAttendance(false)}
          attendanceToConfirmed={attendanceToConfirmed}
          studentList={studentsAttendance}
          updateList={getStudentsAttendance}
        />
      )}
    </div>
  );
};

StudentsAttendanceTable.propTypes = {
  classId: PropTypes.string.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
};

export default StudentsAttendanceTable;
